import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CustomInput from "../../../shared/utils/Input";
import Button from "@mui/material/Button";
import { InputLabel } from "@mui/material";
import { Select, ListItemText, Checkbox } from "@mui/material";
import { updateFormField } from "../../../redux/slices/vendorFormSlice";
import { setFieldError } from "../../../redux/slices/vendorFormSlice";
import Styles from "../../../styles/vendorDashboard.module.css";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import CheckIcon from "@mui/icons-material/Check";
import { validateOperationalDetails } from "./vendorValidation";

const OperationDetailsForm = ({ onNext, onBack }) => {
  const dispatch = useDispatch();
  const { formData, errors } = useSelector((state) => state.vendorForm);

  const handleBlur = (e) => {
    const { name, value } = e.target;

    const validationMessages = {
      // GSTIN: "GSTIN required.",
      // add more messages
      typeOfBusiness: "Please select the type of business",
      avgTurnoverLast3Years: "Please enter the average turnover",
      productionArea: "Please enter the production area.",
      numberOfEmployees: "Please enter number of employees.",
      numberOfSkilledManpower: "Please enter number of skilled manpower.",
      numberOfUnskilledManpower: "Please enter number of unskilled manpower.",
    };

    if (name === "GSTIN") {
      if (value.length !== 15) {
        dispatch(
          setFieldError({
            name,
            message: "Enter a valid 15-digit number",
          })
        );
      }
    }

    if (validationMessages[name]) {
      const isEmpty =
        typeof value === "string"
          ? value.trim() === ""
          : Array.isArray(value)
          ? value.length === 0
          : !value;

      if (isEmpty) {
        dispatch(setFieldError({ name, message: validationMessages[name] }));
      } else {
        dispatch(setFieldError({ name, message: "" }));
      }
    }

    // if (saveButtonClicked) {
    //   dispatch(validateAllFields());
    // }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "scalingFactors") {
      dispatch(updateFormField({ name, value })); // value is already an array
      return;
    }

    if (name === "registeredInESIPF") {
      dispatch(updateFormField({ name, value }));
      return;
    }

    if (
      name === "numberOfEmployees" ||
      name === "numberOfSkilledManpower" ||
      name === "numberOfUnskilledManpower" ||
      name === "avgTurnoverLast3Years"
    ) {
      // Allow only empty string (for clearing) or positive integers
      if (value === "" || /^[0-9]+$/.test(value)) {
        dispatch(updateFormField({ name, value }));
      }
      return;
    }
    if (name === "productionArea") {
      if (!/^\d*$/.test(value)) return; // Block non-numeric input
    }

    if (!value.startsWith(" ")) {
      dispatch(updateFormField({ name, value }));
    }
  };

  const handleSubmit = () => {
    // dispatch(setSaveButtonClicked(true));
    const isValid = validateOperationalDetails(formData, dispatch);

    if (isValid) {
      console.log("Submitting", formData);
      onNext();
    }
  };

  return (
    <div className=" max-w-md ">
      <div
        onClick={onBack}
        className=" w-fit p-1 px-1.5 mb-4 rounded-full bg-gray-100 cursor-pointer"
      >
        <ArrowBackIcon fontSize="small" />
      </div>
      <div>
        <h1 className="text-3xl font-bold mb-4">Operational Details</h1>
        <p className="mb-6">
          Please tell us more about your organisation to personalise your data
          and for seamless interaction.
        </p>
      </div>
      <div
        className="overflow-auto"
        style={{ maxHeight: "calc(100vh - 200px)" }}
      >
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Grid container spacing={2}>
            {/* type of business field */}
            <Grid item xs={12}>
              <InputLabel
                sx={{
                  color: errors?.typeOfBusiness
                    ? "var(--error-color)"
                    : "var(--heading-color)",
                  fontWeight: 500, // example of additional style
                }}
              >
                Type of Business *
              </InputLabel>
              <FormControl
                fullWidth
                size="small"
                sx={{ marginTop: "0.5rem" }}
                error={errors.typeOfBusiness}
                errorMessage={
                  errors.typeOfBusiness ? (
                    <span style={{ color: "var(--error-color)" }}>
                      Please Select type of business
                    </span>
                  ) : null
                }
                value={formData?.typeOfBusiness}
              >
                <Select
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Set border color
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on focus
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on hover
                    },
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#b0b0b0", // Ensure hover state is consistent
                      },
                    },
                  }}
                  style={{ color: formData.typeOfBusiness ? "" : "#a2a2a2" }}
                  displayEmpty
                  name="typeOfBusiness"
                  onBlur={handleBlur}
                  value={formData?.typeOfBusiness}
                  onChange={handleChange}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <p className="text-[1rem] text-[var(--light-gray)]">
                          Type of Business
                        </p>
                      );
                    }

                    return selected;
                  }}
                >
                  <MenuItem
                    value="One Person Company"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    One Person Company
                    {formData?.typeOfBusiness === "One Person Company" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>

                  <MenuItem
                    value="Sole Proprietor"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    Sole Proprietor
                    {formData?.typeOfBusiness === "Sole Proprietor" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>

                  <MenuItem
                    value="Partnership"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    Partnership
                    {formData?.typeOfBusiness === "Partnership" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>

                  <MenuItem
                    value="Foreign Company"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    Foreign Company
                    {formData?.typeOfBusiness === "Foreign Company" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>

                  <MenuItem
                    value="LLP"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    LLP
                    {formData?.typeOfBusiness === "LLP" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>

                  <MenuItem
                    value="Private Limited Company"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    Private Limited Company
                    {formData?.typeOfBusiness === "Private Limited Company" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>

                  <MenuItem
                    value="Limited Company"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    Limited Company
                    {formData?.typeOfBusiness === "Limited Company" && (
                      <CheckIcon sx={{ color: "#418cff" }} />
                    )}
                  </MenuItem>
                </Select>
              </FormControl>
              {errors.typeOfBusiness && (
                <p className="font-normal text-[0.75rem] leading-[1.66] tracking-[0.03em] text-left mt-[4px] mr-[14px] mb-0 ml-[14px] text-[var(--error-color)]">
                  {errors.typeOfBusiness}
                </p>
              )}
            </Grid>
            {/* average turnover field */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Average Turnover"
                title="Turnover in lakhs (Average of 3 Years) *"
                size="small"
                name="avgTurnoverLast3Years"
                required
                value={formData?.avgTurnoverLast3Years}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.avgTurnoverLast3Years}
                errorMessage={errors.avgTurnoverLast3Years}
              />
            </Grid>
            {/* Registered in ESIC/PF */}
            <Grid item xs={12}>
              <InputLabel
                className={`${Styles.labelColor} ${
                  errors?.state
                    ? "text-[var(--error-color)]"
                    : "text-[var(--heading-color)]"
                }`}
              >
                Registered in ESI/PF
              </InputLabel>
              <FormControl
                fullWidth
                size="small"
                sx={{ marginTop: "0.5rem" }}
                error={errors.registeredInESIPF}
                errorMessage={errors.registeredInESIPF}
                value={formData?.registeredInESIPF}
              >
                <Select
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Set border color
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on focus
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on hover
                    },
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#b0b0b0", // Ensure hover state is consistent
                      },
                    },
                  }}
                  style={{ color: formData.registeredInESIPF ? "" : "#a2a2a2" }}
                  displayEmpty
                  name="registeredInESIPF"
                  onBlur={handleBlur}
                  value={formData?.registeredInESIPF}
                  onChange={handleChange}
                  renderValue={(selected) =>
                    selected === true
                      ? "Yes"
                      : selected === false
                      ? "No"
                      : "Select"
                  }
                >
                  <MenuItem value={true}>
                    Yes
                    {formData?.registeredInESIPF === true && (
                      <CheckIcon
                        sx={{ marginLeft: "auto", color: "#418cff" }}
                      />
                    )}
                  </MenuItem>
                  <MenuItem value={false}>
                    No
                    {formData?.registeredInESIPF === false && (
                      <CheckIcon
                        sx={{ marginLeft: "auto", color: "#418cff" }}
                      />
                    )}
                  </MenuItem>
                </Select>
              </FormControl>
              {errors.registeredInESIPF && (
                <p className="font-normal text-[0.75rem] leading-[1.66] tracking-[0.03em] text-left mt-[4px] mr-[14px] mb-0 ml-[14px] text-[var(--error-color)]">
                  {errors.registeredInESIPF}
                </p>
              )}
            </Grid>
            {/* production Area */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Production Area"
                title="Production Area (Sq.Ft.) *"
                size="small"
                name="productionArea"
                value={formData?.productionArea}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.productionArea}
                errorMessage={errors.productionArea}
              />
            </Grid>
            {/* Number of employees */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter total number of employees"
                title="Number of Employees *"
                size="small"
                name="numberOfEmployees"
                value={formData?.numberOfEmployees}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.numberOfEmployees}
                errorMessage={errors.numberOfEmployees}
              />
            </Grid>
            {/* Number of Skilled employees */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter number of skilled employees"
                title="Number of Skilled Manpower *"
                size="small"
                name="numberOfSkilledManpower"
                required
                value={formData?.numberOfSkilledManpower}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.numberOfSkilledManpower}
                errorMessage={errors.numberOfSkilledManpower}
              />
            </Grid>
            {/* Number of Unskilled employees */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter number of unskilled employees"
                title="Number of UnSkilled Manpower *"
                size="small"
                name="numberOfUnskilledManpower"
                required
                value={formData?.numberOfUnskilledManpower}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.numberOfUnskilledManpower}
                errorMessage={errors.numberOfUnskilledManpower}
              />
            </Grid>
            {/* Factors that will help you scale */}
            <Grid item xs={12}>
              <InputLabel
                // className={`${Styles.labelColor} ${
                //   errors?.scalingFactors
                //     ? "text-[var(--error-color)]"
                //     : "text-[var(--heading-color)]"
                // }`}
                sx={{
                  color: errors?.scalingFactors
                    ? "var(--error-color)"
                    : "var(--heading-color)",
                  fontWeight: 500, // example of additional style
                }}
              >
                Factors that will help you scale *
              </InputLabel>
              <FormControl
                fullWidth
                size="small"
                sx={{ marginTop: "0.5rem" }}
                error={errors.scalingFactors}
                // errorMessage={
                //   errors.scalingFactors ? (
                //     <span style={{ color: "var(--error-color)" }}>
                //       Please Select type of business
                //     </span>
                //   ) : null
                // }
                value={formData?.scalingFactors}
              >
                <Select
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Set border color
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on focus
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on hover
                    },
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#b0b0b0", // Ensure hover state is consistent
                      },
                    },
                  }}
                  style={{ color: formData.scalingFactors ? "" : "#a2a2a2" }}
                  displayEmpty
                  name="scalingFactors"
                  onBlur={handleBlur}
                  value={formData?.scalingFactors}
                  onChange={handleChange}
                  multiple={true}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <p className="text-[1rem] text-[#a2a2a2]">
                          Choose Scaling Factors
                        </p>
                      );
                    }

                    return selected.join(",");
                  }}
                >
                  <MenuItem value="Skilled manpower">
                    <Checkbox
                      checked={formData?.scalingFactors.includes(
                        "Skilled manpower"
                      )}
                    />
                    <ListItemText primary="Skilled manpower" />
                  </MenuItem>
                  <MenuItem value="Capital">
                    <Checkbox
                      checked={formData?.scalingFactors.includes("Capital")}
                    />
                    <ListItemText primary="Capital" />
                  </MenuItem>
                  <MenuItem value="Marketing">
                    <Checkbox
                      checked={formData?.scalingFactors.includes("Marketing")}
                    />
                    <ListItemText primary="Marketing" />
                  </MenuItem>
                  <MenuItem value="Mfg. Capability">
                    <Checkbox
                      checked={formData?.scalingFactors.includes(
                        "Mfg. Capability"
                      )}
                    />
                    <ListItemText primary="Mfg. Capability" />
                  </MenuItem>
                  <MenuItem value="Machinery">
                    <Checkbox
                      checked={formData?.scalingFactors.includes("Machinery")}
                    />
                    <ListItemText primary="Machinery" />
                  </MenuItem>
                  <MenuItem value="Personal Factors">
                    <Checkbox
                      checked={formData?.scalingFactors.includes(
                        "Personal Factors"
                      )}
                    />
                    <ListItemText primary="Personal Factors" />
                  </MenuItem>
                </Select>
              </FormControl>
              {errors.scalingFactors && (
                <p className="font-normal text-[0.75rem] leading-[1.66] tracking-[0.03em] text-left mt-[4px] mr-[14px] mb-0 ml-[14px] text-[var(--error-color)]">
                  {errors.scalingFactors}
                </p>
              )}
            </Grid>
          </Grid>
        </Box>

        <div className=" mt-4 flex justify-end">
          <Button onClick={handleSubmit} variant="contained">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OperationDetailsForm;
